import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  QueryOptions as BaseQueryOptions,
  OperationVariables
} from "@apollo/client"
import {Axios, AxiosResponse, Method} from "axios"
import {onError} from "@apollo/client/link/error"
import {cloneDeep} from "lodash"
import logger from "../../logger"
import "cross-fetch/polyfill"
import http from "../../http/client"
import {setContext} from "@apollo/client/link/context"
import {getAuthToken} from "utils/msteams"

export interface QueryOptions<TVariables =
  OperationVariables> extends Omit<BaseQueryOptions<TVariables>, "query" |
  "variables"> {
}

const omitTypename = (key: string, value: any) => {
  return key === "__typename" ? undefined : value
}

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
  }

  return forward(operation)
})

const authLink = setContext(async () => {
  const adToken = await getAuthToken()

  return {
    headers: {
      "authorization": adToken,
      "apollographql-client-name": "teams-bot-fe",
      "apollographql-client-version": "1.0.0"
    }
  }
})

const errorLink = onError((error) => {
  if (error.graphQLErrors) {
    error.graphQLErrors.forEach(({message, locations, path}) =>
      logger.info(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    )
  }

  // @ts-ignore
  if (error.networkError?.response) {
    logger.info(`[Network error]: ${error.networkError}`)
  }
})

type FetchMethod = (url: string, init?: RequestInit) => Promise<Response>

function newAxiosFetch(client: Axios): FetchMethod {
  return async (url: string, init?: RequestInit): Promise<Response> => {
    const reqHeaders = (cloneDeep(init?.headers) || {}) as Record<string, string>
    let res: AxiosResponse

    res = await client.request({
      url,
      method: (init?.method as Method) || "GET",
      data: String(init?.body),
      headers: reqHeaders
    })

    // Convert the Axios style response into a `fetch` style response
    const body = typeof res.data === "object" ? JSON.stringify(res.data) : res.data

    return new Response(body, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers as Record<string, string>
    })
  }
}

const cache = new InMemoryCache()

const httpLink = new HttpLink({
  fetch: newAxiosFetch(http),
  uri: "/api/v1/graph"
})

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(omitTypenameLink).concat(httpLink),
  cache
})

export default client